import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { listStorage, type StorageListing, type StorageVolume } from "@/lib/api";
import { Spinner } from "@/components/Page";
import { cn } from "@/lib/utils";

// Lazy so CodeMirror + react-markdown only load when the Workspace view opens.
const PlaybookEditor = lazy(() =>
  import("@/components/PlaybookEditor").then((m) => ({ default: m.PlaybookEditor })),
);
const PlaybookAuthor = lazy(() =>
  import("@/components/PlaybookAuthor").then((m) => ({ default: m.PlaybookAuthor })),
);

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const VOLUMES: { id: StorageVolume; label: string }[] = [
  { id: "playbook", label: "Playbook" },
  { id: "skills", label: "Skills" },
  { id: "state", label: "State" },
];

// UsageInline is the compact storage gauge shown in the editor toolbar.
function UsageInline({ usage }: { usage: StorageListing["usage"] }) {
  if (!usage.total_bytes) return null;
  const pct = Math.min(100, Math.round((usage.used_bytes / usage.total_bytes) * 100));
  const low = usage.free_bytes < usage.total_bytes * 0.1;
  return (
    <div
      className="hidden items-center gap-1.5 sm:flex"
      title={`${formatBytes(usage.free_bytes)} free of ${formatBytes(usage.total_bytes)} (a few MB is reserved for filesystem metadata)`}
    >
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
        <div className={cn("h-full rounded-full", low ? "bg-destructive" : "bg-primary")} style={{ width: `${pct}%` }} />
      </div>
      <span className="whitespace-nowrap text-[11px] text-muted-foreground">{formatBytes(usage.free_bytes)} free</span>
    </div>
  );
}

// WorkspaceTab is a full-bleed, app-like editing surface (the Layout bleeds this
// route): the file tree sits flush against the app sidebar and the editor fills
// the rest. playbook, skills, and state are subdirs of the agent's single mutable
// volume; a tab group + the storage gauge live in the editor's top toolbar.
export function WorkspaceTab({
  projectId,
  agentName,
  subdomain,
}: {
  projectId: string;
  agentName: string;
  subdomain: string;
}) {
  const [volume, setVolume] = useState<StorageVolume>("playbook");
  const [usage, setUsage] = useState<StorageListing["usage"] | null>(null);
  const [authorOpen, setAuthorOpen] = useState(false);

  const refreshUsage = useCallback(async () => {
    try {
      const l = await listStorage(projectId, "state", "");
      setUsage(l.usage);
    } catch {
      // Best-effort: a never-deployed agent or a viewer without access has none.
    }
  }, [projectId]);

  useEffect(() => {
    void refreshUsage();
  }, [refreshUsage]);

  // Volume tabs + the storage gauge, injected into the editor's single top toolbar.
  const toolbarStart = (
    <div className="flex min-w-0 items-center gap-2">
      <div className="flex shrink-0 overflow-hidden rounded-md border">
        {VOLUMES.map((v) => (
          <button
            key={v.id}
            onClick={() => setVolume(v.id)}
            className={cn(
              "px-2.5 py-1 text-xs font-medium transition-colors",
              volume === v.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted",
            )}
          >
            {v.label}
          </button>
        ))}
      </div>
      {usage && <UsageInline usage={usage} />}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <Suspense
        fallback={
          <div className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Spinner /> Loading editor…
          </div>
        }
      >
        {/* Unmounted while the author overlay is open: only one editor is ever
            live (no duplicate global shortcuts / hidden focusable pane), and it
            remounts fresh on close to show the assistant's writes. key=volume
            also remounts on volume switch. */}
        {!authorOpen && (
          <PlaybookEditor
            key={volume}
            projectId={projectId}
            volume={volume}
            embedded
            toolbarStart={toolbarStart}
            onChanged={refreshUsage}
            onGenerateAI={volume === "playbook" ? () => setAuthorOpen(true) : undefined}
          />
        )}
      </Suspense>

      {authorOpen && (
        <Suspense fallback={null}>
          <PlaybookAuthor
            projectId={projectId}
            agentName={agentName}
            subdomain={subdomain}
            onClose={() => {
              setAuthorOpen(false);
              void refreshUsage();
            }}
          />
        </Suspense>
      )}
    </div>
  );
}
